<template>
  <el-dialog :title="dialogTitle" :visible="visible" width="780px" append-to-body @close="close">
    <el-descriptions v-if="record && Object.keys(record).length" :column="2" border>
      <el-descriptions-item label="单号">{{ orderNo }}</el-descriptions-item>
      <el-descriptions-item label="单据类型">{{ typeText }}</el-descriptions-item>

      <template v-if="type === 'assetClaim'">
        <el-descriptions-item label="申请状态"><el-tag size="mini" :type="claimStatusTag(record.status)">{{ claimStatusText(record.status) }}</el-tag></el-descriptions-item>
        <el-descriptions-item label="申请时间">{{ record.createTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="申请人">{{ record.applicantUserNameSnapshot || '-' }}</el-descriptions-item>
        <el-descriptions-item label="部门">{{ record.applicantDeptNameSnapshot || '-' }}</el-descriptions-item>
        <el-descriptions-item label="管理归属">{{ managementText(record.managementType) }}</el-descriptions-item>
        <el-descriptions-item label="资产分类">{{ record.categoryNameSnapshot || '-' }}</el-descriptions-item>
        <el-descriptions-item label="申请资产" :span="2">{{ record.requestedAssetName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="领用用途" :span="2">{{ record.purpose || '-' }}</el-descriptions-item>
        <el-descriptions-item label="期望日期">{{ record.expectedDate || '-' }}</el-descriptions-item>
        <el-descriptions-item label="分配资产">{{ assignedAssetText(record) }}</el-descriptions-item>
        <el-descriptions-item label="处理人">{{ record.handledByNameSnapshot || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理时间">{{ record.handledTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理说明" :span="2">{{ record.handleRemark || '-' }}</el-descriptions-item>
        <el-descriptions-item label="完成时间" :span="2">{{ record.completedTime || '-' }}</el-descriptions-item>
      </template>

      <template v-else-if="['assetOperation', 'assetReturn'].includes(type)">
        <el-descriptions-item label="当前状态">{{ operationStatusText(record.status) }}</el-descriptions-item>
        <el-descriptions-item label="提交时间">{{ record.requestedTime || record.createTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="资产编号">{{ assetCode }}</el-descriptions-item>
        <el-descriptions-item label="资产名称">{{ assetName }}</el-descriptions-item>
        <el-descriptions-item label="流转类型">{{ operationTypeText(record.operationType) }}</el-descriptions-item>
        <el-descriptions-item label="提交人">{{ record.requestedByNameSnapshot || record.requestedByName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="原保管人">{{ record.fromUserNameSnapshot || record.assetFromUserName || '资产库' }}</el-descriptions-item>
        <el-descriptions-item label="接收人">{{ record.toUserNameSnapshot || '资产库' }}</el-descriptions-item>
        <el-descriptions-item label="原位置">{{ record.fromLocation || record.assetFromLocation || record.assetStorageLocation || '-' }}</el-descriptions-item>
        <el-descriptions-item label="交付位置">{{ record.toLocation || record.assetToLocation || record.assetStorageLocation || '-' }}</el-descriptions-item>
        <el-descriptions-item label="提交说明" :span="2">{{ record.remark || record.requestRemark || '-' }}</el-descriptions-item>
        <el-descriptions-item v-if="record.confirmRemark" label="确认说明" :span="2">{{ record.confirmRemark }}</el-descriptions-item>
      </template>

      <template v-else-if="['assetRepair', 'assetRepairTask'].includes(type)">
        <el-descriptions-item label="当前进度"><el-tag size="mini" :type="repairStatusTag(record.status)">{{ repairStatusText(record.status) }}</el-tag></el-descriptions-item>
        <el-descriptions-item label="提交时间">{{ record.reportedTime || record.createTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="报修资产">{{ assetCode }} / {{ assetName }}</el-descriptions-item>
        <el-descriptions-item label="管理归属">{{ managementText(record.managementType) }}</el-descriptions-item>
        <el-descriptions-item label="报修人">{{ record.reportedByNameSnapshot || record.requestedByName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="损坏程度">{{ damageText(record.damageLevel) }}</el-descriptions-item>
        <el-descriptions-item label="故障描述" :span="2">{{ record.faultDescription || record.requestRemark || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理结果">{{ decisionText(record.inspectionDecision) }}</el-descriptions-item>
        <el-descriptions-item label="处理人">{{ record.inspectorNameSnapshot || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理时间">{{ record.inspectionTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="维修日期">{{ record.repairDate || '-' }}</el-descriptions-item>
        <el-descriptions-item label="处理说明" :span="2">{{ record.inspectionRemark || '-' }}</el-descriptions-item>
        <el-descriptions-item v-if="record.repairResult" label="维修结果" :span="2">{{ record.repairResult }}</el-descriptions-item>
        <el-descriptions-item v-if="record.disposalMethod" label="报废处置" :span="2">{{ record.disposalMethod }} / {{ record.disposalDate || '-' }} / {{ record.disposalByNameSnapshot || '-' }}</el-descriptions-item>
      </template>

      <template v-else-if="type === 'assetStocktake'">
        <el-descriptions-item label="当前状态">{{ Number(record.status) === 10 ? '待保管人确认盘点' : '已确认盘点' }}</el-descriptions-item>
        <el-descriptions-item label="资产编号">{{ record.assetCodeSnapshot || record.assetCode || '-' }}</el-descriptions-item>
        <el-descriptions-item label="资产名称">{{ record.assetNameSnapshot || record.assetName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="账面保管人">{{ record.expectedUserNameSnapshot || '资产管理员' }}</el-descriptions-item>
        <el-descriptions-item label="账面位置">{{ record.expectedLocation || '-' }}</el-descriptions-item>
        <el-descriptions-item label="实际位置">{{ record.actualLocation || '-' }}</el-descriptions-item>
        <el-descriptions-item label="盘点结果">{{ stocktakeResultText(record.result) }}</el-descriptions-item>
        <el-descriptions-item label="确认时间">{{ record.confirmTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="盘点说明" :span="2">{{ record.remark || '-' }}</el-descriptions-item>
      </template>

      <template v-else>
        <el-descriptions-item label="发生时间">{{ record.eventTime || record.createTime || '-' }}</el-descriptions-item>
        <el-descriptions-item label="操作人">{{ record.operatorName || '-' }}</el-descriptions-item>
        <el-descriptions-item label="事项" :span="2">{{ record.title || record.toDoContent || '-' }}</el-descriptions-item>
        <el-descriptions-item label="详细信息" :span="2">{{ record.description || '-' }}</el-descriptions-item>
        <el-descriptions-item label="说明" :span="2">{{ record.remark || '-' }}</el-descriptions-item>
      </template>
    </el-descriptions>

    <template v-if="['assetRepair', 'assetRepairTask'].includes(type) && record.workOrderId">
      <div class="detail-section-title">报废审批流程</div>
      <div class="approval-order">审批单号：SP{{ record.workOrderId }}</div>
      <approve-info
        :key="record.workOrderId"
        :target-id="record.workOrderId"
        target-type="assetRepair"
        :allow-transfer="false"
        @success="$emit('success')"
      />
    </template>

    <span slot="footer"><el-button @click="close">关闭</el-button></span>
  </el-dialog>
</template>

<script>
import ApproveInfo from '@/views/crm/components/approveInfo'

export default {
  name: 'AssetOrderDetailDialog',
  components: { ApproveInfo },
  props: {
    visible: { type: Boolean, default: false },
    type: { type: String, default: '' },
    record: { type: Object, default: () => ({}) }
  },
  computed: {
    dialogTitle() { return `${this.typeText}详情` },
    typeText() {
      return ({ assetClaim: '领用申请单', assetOperation: '资产流转单', assetReturn: '资产归还单', assetRepair: '报修报废单', assetRepairTask: '报修报废单', assetStocktake: '资产盘点单', assetLifecycle: '资产履历单' })[this.type] || '资产单据'
    },
    orderNo() { return this.record.requestNo || this.record.operationNo || this.record.recordNo || this.record.taskNo || this.record.targetNo || this.record.relatedNo || '-' },
    assetCode() { return this.record.assetCodeSnapshot || this.record.assetCode || '-' },
    assetName() { return this.record.assetNameSnapshot || this.record.assetName || '-' }
  },
  methods: {
    close() { this.$emit('update:visible', false) },
    managementText(value) { return ({ HR: '人事行政', WAREHOUSE: '仓库' })[value] || '-' },
    assignedAssetText(row) { return row.assignedAssetId ? `${row.assignedAssetCodeSnapshot || '-'} / ${row.assignedAssetNameSnapshot || '-'}` : '-' },
    claimStatusText(value) { return ({ 10: '待资产管理员处理', 20: '待领用人确认', 30: '已完成', 40: '已驳回', 50: '已取消' })[Number(value)] || '-' },
    claimStatusTag(value) { return ({ 10: 'warning', 20: 'primary', 30: 'success', 40: 'danger', 50: 'info' })[Number(value)] || 'info' },
    operationTypeText(value) { return ({ 10: '领用', 20: '调拨', 30: '归还' })[Number(value)] || '资产流转' },
    operationStatusText(value) { return ({ 10: '待领用人确认接收', 20: '待原保管人确认转出', 21: '待新保管人确认接收', 30: '待资产管理员验收入库', 40: '已完成', 50: '已拒绝' })[Number(value)] || '资产流转待处理' },
    damageText(value) { return ({ 10: '轻微', 20: '中度', 30: '严重', 40: '报废' })[Number(value)] || '-' },
    decisionText(value) { return ({ REPAIR: '记录维修', SCRAP: '申请报废' })[value] || '待处理' },
    repairStatusText(value) { return ({ 10: '待处理', 20: '维修完成', 30: '报废审批中', 40: '审批通过待处置', 50: '审批驳回待处理', 60: '已报废' })[Number(value)] || '-' },
    repairStatusTag(value) { return ({ 10: 'warning', 20: 'success', 30: 'warning', 40: 'danger', 50: 'info', 60: '' })[Number(value)] || 'info' },
    stocktakeResultText(value) { return ({ 10: '正常', 20: '位置不符', 30: '损坏', 40: '丢失', 50: '其他异常' })[Number(value)] || '-' }
  }
}
</script>

<style scoped>
.detail-section-title { margin:22px 0 10px; padding-left:9px; border-left:3px solid #409eff; color:#303133; font-weight:600; }
.approval-order { margin-bottom:12px; color:#606266; }
</style>
